<template>
  <PageWrap page-title="Subir Documentación">
    <LoadingIndicator v-if="loadingData" />

    <div v-else>
      <BfTitle size="small" class="mb-3">Encargo: {{chargeNumber}}</BfTitle>

      <label class="file-upload-box">
        <span class="upload-box-inner">
          <i class="upload-box-icon fas fa-image" />
          <span class="upload-box-label">Selecciona una o varias fotos</span>
        </span>
        <input type="file" multiple @change="onFileInputChange" />

        <span v-if="fileError" class="upload-error">{{fileError}}</span>
      </label>

      <BfInput
        v-model="formData.name"
        data-key="titulo"
        :label="nameFieldLabel"
        :placeholder="nameFieldPlaceholder"
        :errors="formErrors"
      />

      <BfSelect
        v-model="formData.category"
        data-key="categoria"
        label="Categoría"
        :options="categorySelectOptions"
        :errors="formErrors"
      />

      <UploadProgressBar
        v-if="submittingForm"
        :count-progress="uploadCountProgress"
        :size-progress="uploadSizeProgress"
        class="mt-2 mb-3"
      />

      <BfButton
        :disabled="!areFiles || submittingForm"
        @click="onSubmitClick">
        {{saveButtonLabel}}
      </BfButton>
    </div>
  </PageWrap>
</template>

<script>
import PageWrap from '@/components/PageWrap';
import LoadingIndicator from '@/components/LoadingIndicator';
import { chargeAPI, documentAPI } from '@/api';
import { get } from '@/utils';
import { uploadChargeDocuments } from '@/utils/abstract';
import UploadProgressBar from '@/components/UploadProgressBar';

export default {
  name: 'UploadChargeDocuments',

  props: {
    from: {
      type: String,
    },
  },

  data() {
    return {
      category: null,
      chargeId: get(this.$route, ['query', 'chargeId'], null),
      chargeNumber: '',
      documentCategories: [],
      formData: {
        category: null,
        files: [],
        name: '',
      },
      formErrors: {},
      loadingData: false,
      submittingForm: false,
      uploadCountProgress: {},
      uploadSizeProgress: {},
    };
  },

  async created() {
    if (!this.chargeId) {
      this.$router.replace({ name: 'charges' });
    } else {
      this.loadingData = true;

      try {
        await Promise.all([this.loadChargeData(), this.loadDocumentCategories()]);
      } catch ({ errors = '' }) {
        if (errors) {
          this.$bfNotify({
            title: 'Error',
            message: errors,
            type: 'error',
          });

          this.$router.replace({ name: 'charges' });
        }
      } finally {
        this.loadingData = false;
      }
    }
  },

  methods: {
    async loadChargeData() {
      const {
        data: [charge],
      } = await chargeAPI.get(this.chargeId);

      this.chargeNumber = charge.numero_encargo;
    },

    async loadDocumentCategories() {
      const { categorias = [] } = await documentAPI.preCreate();

      this.documentCategories = categorias;
    },

    onFileInputChange(event) {
      this.formData = {
        ...this.formData,
        files: event.target.files,
      };
    },

    async onSubmitClick() {
      this.formErrors = {};
      this.submittingForm = true;
      const { name, category, files } = this.formData;

      try {
        const { notificationPayload } = await uploadChargeDocuments({
          files: [...files], // convert FileList to Array
          title: name,
          category,
          chargeId: this.chargeId,
          countProgressCallback: (p) => {
            this.uploadCountProgress = p;
          },
          sizeProgressCallback: (p) => {
            this.uploadSizeProgress = p;
          },
        });

        this.$bfNotify(notificationPayload);

        if (this.from) {
          this.$router.replace({ name: this.from });
        }
      } catch ({ errors }) {
        this.formErrors = errors;
      } finally {
        this.submittingForm = false;
      }
    },
  },

  computed: {
    categorySelectOptions() {
      return this.documentCategories.map((label, index) => ({
        label,
        value: index,
      }));
    },

    fileCount() {
      const files = get(this.formData, ['files'], []);

      return files.length;
    },

    fileError() {
      if (this.formErrors && this.formErrors.file && this.formErrors.file.length) {
        return this.formErrors.file.join(' ');
      }

      return '';
    },

    areFiles() {
      return this.fileCount > 0;
    },

    areMultipleFiles() {
      return this.fileCount > 1;
    },

    saveButtonLabel() {
      return `Subir Fichero${this.areMultipleFiles ? 's' : ''}`;
    },

    nameFieldLabel() {
      return !this.areMultipleFiles ? 'Nombre' : 'Prefijo';
    },

    nameFieldPlaceholder() {
      return !this.areMultipleFiles
        ? 'Escribe el nombre del fichero'
        : 'Escribe el prefijo que quieras aplicar a varios ficheros';
    },
  },

  components: {
    LoadingIndicator,
    PageWrap,
    UploadProgressBar,
  },
};
</script>

<style lang="scss" scoped>
.file-upload-box {
  display: block;
  padding: 12px 10px;
  margin-bottom: 20px;
  border: 2px solid var(--bf-color-primary);
  color: 2px solid var(--bf-color-primary);
  text-align: center;
  cursor: pointer;

  .upload-box-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
  }

  .upload-box-icon {
    font-size: 26px;
    margin-right: 12px;
  }

  .upload-box-label {
    font-size: 16px;
    font-weight: var(--bf-font-weight-semibold);
  }

  .upload-error {
    margin-top: 20px;
    display: block;
    color: var(--bf-color-danger);
    font-weight: var(--bf-font-weight-semibold);
  }
}
</style>
